import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { associationsService } from '../services/associations.service';
import type {
  CreateAssociationGroupDto,
  UpdateAssociationGroupDto,
  AddProductsToGroupDto,
} from '@/lib/types';

/**
 * Hook to fetch all association groups
 */
export function useAssociationGroups() {
  return useQuery({
    queryKey: ['association-groups'],
    queryFn: () => associationsService.getAssociationGroups(),
  });
}

/**
 * Hook to create a new association group
 */
export function useCreateAssociationGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssociationGroupDto) =>
      associationsService.createAssociationGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['association-groups'] });
    },
  });
}

/**
 * Hook to add products to an existing group
 */
export function useAddProductsToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddProductsToGroupDto) =>
      associationsService.addProductsToGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['association-groups'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
    },
  });
}

/**
 * Hook to update an association group
 */
export function useUpdateAssociationGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAssociationGroupDto) =>
      associationsService.updateAssociationGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['association-groups'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
    },
  });
}

/**
 * Hook to remove a product from a group
 */
export function useRemoveProductFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, productRefId }: { groupId: number; productRefId: string }) =>
      associationsService.removeProductFromGroup(groupId, productRefId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['association-groups'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
    },
  });
}

/**
 * Hook to delete an entire association group
 */
export function useDeleteAssociationGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: number) => associationsService.deleteAssociationGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['association-groups'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
    },
  });
}
